import React, { useState, useRef, useEffect, useCallback } from 'react';
import Header from '../components/layout/Header';
import { useAuth } from '../context/AuthContext';
import { updateProfile, changePassword, uploadAvatar, getSessions, revokeSessionById, revokeAllSessions } from '../utils/api';
import { formatPhone } from '../utils/helpers';
import ConfirmModal from '../components/common/ConfirmModal';
import PasswordStrengthMeter from '../components/common/PasswordStrengthMeter';
import { Camera, Save, Lock, User, Mail, Phone, Briefcase, Eye, EyeOff, Monitor, Smartphone, LogOut, Trash2, Shield } from 'lucide-react';
import toast from 'react-hot-toast';

export default function ProfilePage() {
  const { user, refreshUser, logout } = useAuth();
  const fileInputRef = useRef(null);

  // Profile form
  const [form, setForm] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    title: user?.title || '',
  });
  const [saving, setSaving] = useState(false);

  // Avatar
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Password
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [pwSaving, setPwSaving] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);

  // Sessions
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  const loadSessions = useCallback(async () => {
    setSessionsLoading(true);
    try {
      const data = await getSessions();
      setSessions(data || []);
    } catch { /* silent */ }
    setSessionsLoading(false);
  }, []);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  const handleRevokeSession = async (id) => {
    try {
      await revokeSessionById(id);
      toast.success('Session revoked');
      loadSessions();
    } catch { toast.error('Failed to revoke session'); }
  };

  const handleRevokeAll = () => {
    setConfirmAction({
      title: 'Log Out All Devices',
      message: 'This will end all active sessions including this one. You will be redirected to the login page.',
      confirmLabel: 'Log Out All',
      danger: true,
      onConfirm: async () => {
        try {
          await revokeAllSessions();
          toast.success('All sessions revoked');
          logout();
        } catch { toast.error('Failed to revoke sessions'); }
      },
    });
  };

  const initials = `${(user?.first_name || '')[0] || ''}${(user?.last_name || '')[0] || ''}`.toUpperCase() || '?';
  const avatarUrl = avatarPreview || (user?.avatar ? user.avatar : null);

  // ── Save profile ──
  const handleSaveProfile = async () => {
    if (!form.first_name.trim()) return toast.error('First name required');
    if (!form.email.trim()) return toast.error('Email required');
    const emailChanged = form.email.trim().toLowerCase() !== (user?.email || '').toLowerCase();
    setSaving(true);
    try {
      await updateProfile(form);
      await refreshUser();
      if (emailChanged) {
        toast.success('Profile updated — use ' + form.email.trim().toLowerCase() + ' to log in from now on', { duration: 6000 });
      } else {
        toast.success('Profile updated');
      }
    } catch (err) {
      toast.error(err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // ── Avatar upload ──
  const handleAvatarClick = () => fileInputRef.current?.click();

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate
    if (!file.type.startsWith('image/')) return toast.error('Please select an image file');
    if (file.size > 5 * 1024 * 1024) return toast.error('Image must be under 5MB');

    // Preview
    const reader = new FileReader();
    reader.onload = (ev) => setAvatarPreview(ev.target.result);
    reader.readAsDataURL(file);

    // Upload
    setUploading(true);
    try {
      const res = await uploadAvatar(file);
      await refreshUser();
      toast.success('Profile picture updated');
    } catch (err) {
      toast.error(err?.message || 'Upload failed');
      setAvatarPreview(null);
    } finally {
      setUploading(false);
    }
  };

  // ── Change password ──
  const handleChangePassword = async () => {
    if (!pwForm.current_password) return toast.error('Enter current password');
    if (!pwForm.new_password) return toast.error('Enter new password');
    if (pwForm.new_password.length < 12) return toast.error('New password must be at least 12 characters');
    if (pwForm.new_password !== pwForm.confirm_password) return toast.error('Passwords do not match');

    setPwSaving(true);
    try {
      await changePassword({
        current_password: pwForm.current_password,
        new_password: pwForm.new_password,
      });
      setPwForm({ current_password: '', new_password: '', confirm_password: '' });
      toast.success('Password changed successfully');
    } catch (err) {
      toast.error(err?.message || 'Failed to change password');
    } finally {
      setPwSaving(false);
    }
  };

  return (
    <>
      <Header title="My Profile" />
      <div className="app-content" style={{ maxWidth: '800px' }}>

        {/* ── Avatar + Name Header ── */}
        <div className="card" style={{ padding: '30px', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
            {/* Avatar */}
            <div
              onClick={handleAvatarClick}
              style={{
                width: '100px',
                height: '100px',
                borderRadius: '50%',
                background: avatarUrl ? `url(${avatarUrl}) center/cover` : 'var(--zeus-gold-dim)',
                border: '3px solid var(--zeus-gold)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                position: 'relative',
                flexShrink: 0,
                overflow: 'hidden',
              }}
            >
              {!avatarUrl && (
                <span style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--zeus-gold)' }}>
                  {initials}
                </span>
              )}
              {/* Hover overlay */}
              <div style={{
                position: 'absolute',
                inset: 0,
                background: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: 0,
                transition: 'opacity 0.2s',
                borderRadius: '50%',
              }}
                onMouseOver={(e) => e.currentTarget.style.opacity = 1}
                onMouseOut={(e) => e.currentTarget.style.opacity = 0}
              >
                <Camera size={24} style={{ color: '#fff' }} />
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleAvatarChange}
              />
            </div>

            {/* Name + Role */}
            <div>
              <h2 style={{ margin: 0, fontSize: '1.5rem', fontFamily: 'var(--font-display)', fontWeight: 400, color: 'var(--text-primary)' }}>
                {user?.first_name} {user?.last_name}
              </h2>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '4px' }}>
                <span className="badge" style={{
                  background: 'var(--zeus-gold-dim)',
                  color: 'var(--zeus-gold)',
                  fontSize: '0.78rem',
                  textTransform: 'capitalize',
                }}>
                  {user?.role}
                </span>
                {user?.title && (
                  <span style={{ color: 'var(--text-muted)', fontSize: '0.88rem' }}>
                    {user.title}
                  </span>
                )}
              </div>
              <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: '6px' }}>
                {user?.email}
              </div>
              {uploading && (
                <div style={{ color: 'var(--zeus-gold)', fontSize: '0.82rem', marginTop: '4px' }}>
                  Uploading...
                </div>
              )}
              <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '6px' }}>
                Click photo to change profile picture
              </div>
            </div>
          </div>
        </div>

        {/* ── Profile Information ── */}
        <div className="card" style={{ padding: '24px', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
            <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <User size={18} style={{ color: 'var(--zeus-gold)' }} /> Profile Information
            </h3>
            <button className="btn btn-primary btn-sm" onClick={handleSaveProfile} disabled={saving}>
              <Save size={14} /> {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="form-group">
              <label className="form-label">First Name *</label>
              <input
                className="form-input"
                value={form.first_name}
                onChange={(e) => setForm(prev => ({ ...prev, first_name: e.target.value }))}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Last Name</label>
              <input
                className="form-input"
                value={form.last_name}
                onChange={(e) => setForm(prev => ({ ...prev, last_name: e.target.value }))}
              />
            </div>

            <div className="form-group">
              <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Mail size={14} /> Email *
              </label>
              <input
                className="form-input"
                type="email"
                value={form.email}
                onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
              />
            </div>

            <div className="form-group">
              <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Phone size={14} /> Phone
              </label>
              <input
                className="form-input"
                value={formatPhone(form.phone)}
                placeholder="(___) ___-____"
                onChange={(e) => setForm(prev => ({ ...prev, phone: formatPhone(e.target.value) }))}
              />
            </div>

            <div className="form-group" style={{ gridColumn: 'span 2' }}>
              <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Briefcase size={14} /> Title / Position
              </label>
              <input
                className="form-input"
                value={form.title}
                onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="e.g. Senior Attorney, Case Manager, Paralegal"
              />
            </div>
          </div>

          <div style={{ marginTop: '12px', padding: '12px', background: 'var(--zeus-navy-light)', borderRadius: 'var(--radius-md)', border: '1px solid var(--zeus-border)' }}>
            <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
              <strong style={{ color: 'var(--text-secondary)' }}>Role:</strong> {user?.role} · 
              <strong style={{ color: 'var(--text-secondary)' }}> Firm:</strong> {user?.organization?.name || '—'} · 
              <strong style={{ color: 'var(--text-secondary)' }}> Member since:</strong> {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : '—'}
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '4px' }}>
              Role changes can only be made by an Owner or Admin in Settings → Team.
            </div>
          </div>
        </div>

        {/* ── Change Password ── */}
        <div className="card" style={{ padding: '24px' }}>
          <h3 style={{ margin: '0 0 20px', fontSize: '1.1rem', fontWeight: 600, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Lock size={18} style={{ color: 'var(--zeus-gold)' }} /> Change Password
          </h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', maxWidth: '400px' }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Current Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  className="form-input"
                  type={showCurrent ? 'text' : 'password'}
                  value={pwForm.current_password}
                  onChange={(e) => setPwForm(prev => ({ ...prev, current_password: e.target.value }))}
                  placeholder="Enter current password"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrent(!showCurrent)}
                  style={{
                    position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '4px',
                  }}
                >
                  {showCurrent ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">New Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  className="form-input"
                  type={showNew ? 'text' : 'password'}
                  value={pwForm.new_password}
                  onChange={(e) => setPwForm(prev => ({ ...prev, new_password: e.target.value }))}
                  placeholder="At least 12 characters"
                />
                <button
                  type="button"
                  onClick={() => setShowNew(!showNew)}
                  style={{
                    position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '4px',
                  }}
                >
                  {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <PasswordStrengthMeter password={pwForm.new_password} />
            </div>

            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Confirm New Password</label>
              <input
                className="form-input"
                type="password"
                value={pwForm.confirm_password}
                onChange={(e) => setPwForm(prev => ({ ...prev, confirm_password: e.target.value }))}
                placeholder="Re-enter new password"
              />
              {pwForm.new_password && pwForm.confirm_password && pwForm.new_password !== pwForm.confirm_password && (
                <span style={{ fontSize: '0.78rem', color: 'var(--status-red)', marginTop: '4px' }}>
                  Passwords do not match
                </span>
              )}
            </div>

            <button
              className="btn btn-primary"
              onClick={handleChangePassword}
              disabled={pwSaving}
              style={{ alignSelf: 'flex-start', marginTop: '4px' }}
            >
              <Lock size={14} /> {pwSaving ? 'Changing...' : 'Change Password'}
            </button>
          </div>
        </div>

        {/* ── Active Sessions ── */}
        <div className="card" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 className="section-title" style={{ margin: 0 }}>
              <Shield size={16} /> Active Sessions
            </h3>
            {sessions.length > 1 && (
              <button
                className="btn btn-sm"
                onClick={handleRevokeAll}
                style={{ background: 'var(--status-red)', color: '#fff', fontSize: '0.78rem', padding: '6px 14px', borderRadius: '6px', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                <LogOut size={13} /> Log Out All Devices
              </button>
            )}
          </div>

          {sessionsLoading ? (
            <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Loading sessions...</p>
          ) : sessions.length === 0 ? (
            <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>No active sessions found.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {sessions.map((s, i) => {
                const ua = (s.user_agent || '').toLowerCase();
                const isMobile = /mobile|android|iphone|ipad/.test(ua);
                const browser = /chrome/.test(ua) ? 'Chrome' : /firefox/.test(ua) ? 'Firefox' : /safari/.test(ua) ? 'Safari' : /edge/.test(ua) ? 'Edge' : 'Browser';
                const os = /windows/.test(ua) ? 'Windows' : /mac/.test(ua) ? 'macOS' : /linux/.test(ua) ? 'Linux' : /android/.test(ua) ? 'Android' : /iphone|ipad/.test(ua) ? 'iOS' : '';
                const lastActive = s.last_active_at ? new Date(s.last_active_at) : null;
                const isRecent = lastActive && (Date.now() - lastActive.getTime()) < 5 * 60 * 1000;

                return (
                  <div key={s.id} style={{
                    display: 'flex', alignItems: 'center', gap: '12px',
                    padding: '12px 14px', borderRadius: '8px',
                    background: i === 0 ? 'var(--bg-accent, rgba(212,168,83,0.06))' : 'var(--bg-secondary, #f8fafc)',
                    border: i === 0 ? '1px solid var(--accent, #d4a853)' : '1px solid var(--border, #e2e8f0)',
                  }}>
                    <div style={{
                      width: '36px', height: '36px', borderRadius: '8px',
                      background: i === 0 ? 'rgba(212,168,83,0.12)' : 'var(--bg-tertiary, #f1f5f9)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      {isMobile ? <Smartphone size={16} style={{ color: 'var(--text-muted)' }} /> : <Monitor size={16} style={{ color: 'var(--text-muted)' }} />}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                        {browser}{os ? ` on ${os}` : ''}{i === 0 ? ' (this device)' : ''}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '2px' }}>
                        {s.ip_address || 'Unknown IP'}
                        {lastActive && (
                          <span> -- {isRecent ? 'Active now' : `Last active ${lastActive.toLocaleDateString()} ${lastActive.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}</span>
                        )}
                      </div>
                    </div>
                    {i !== 0 && (
                      <button
                        onClick={() => handleRevokeSession(s.id)}
                        title="End this session"
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer', padding: '6px',
                          color: 'var(--status-red)', borderRadius: '6px', flexShrink: 0,
                        }}
                      >
                        <Trash2 size={15} />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '12px' }}>
            Sessions expire after 8 hours of inactivity. Your session will show a warning after 15 minutes of no activity.
          </p>
        </div>
      </div>

      <ConfirmModal
        isOpen={!!confirmAction}
        onClose={() => setConfirmAction(null)}
        onConfirm={async () => { await confirmAction?.onConfirm(); setConfirmAction(null); }}
        title={confirmAction?.title || ''}
        message={confirmAction?.message || ''}
        confirmLabel={confirmAction?.confirmLabel || 'Confirm'}
        danger={confirmAction?.danger !== false}
      />
    </>
  );
}
